import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getMainImage } from '../../../utils/image-utils';
import { getApiEndpoint } from '../../shared/utils/api-url';

interface Product {
  id: number;
  name: string;
  price: number;
  original_price?: number;
  images?: { main: string; colors?: { [color: string]: string } };
  discount_percent?: number;
}

function Catalogo() {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [categoryId, setCategoryId] = useState(searchParams.get('category') || '');

  useEffect(() => {
    // Atualizar categoryId quando searchParams mudar
    const categoryParam = searchParams.get('category');
    setCategoryId(categoryParam || '');
  }, [searchParams]);

  useEffect(() => {
    loadProducts();
  }, [searchQuery, categoryId]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      let url = getApiEndpoint('/products?active=true');
      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }
      if (categoryId) {
        url += `&category_id=${categoryId}`;
      }
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Erro ao carregar produtos');
      }
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: number) => {
    return `R$ ${price.toFixed(2).replace('.', ',')}`;
  };

  const calculateDiscount = (price: number, originalPrice: number) => {
    return Math.round(((originalPrice - price) / originalPrice) * 100);
  };

  return (
    <div className="mobile-content">
      {/* Filters Section */}
      <section className="filters-section">
        <div className="filters-header">
          <h2 className="section-title">Catálogo</h2>
          <button className="filter-btn" id="filter-btn">
            <i className="fas fa-filter"></i>
            Filtros
          </button>
        </div>
        <div className="results-count">
          {loading ? 'Carregando...' : `${products.length} produtos encontrados`}
        </div>
      </section>

      {/* Products Grid */}
      <section className="products-section">
        {loading ? (
          <div className="loading-message">Carregando produtos...</div>
        ) : products.length === 0 ? (
          <div className="empty-message">Nenhum produto encontrado</div>
        ) : (
          <div className="products-grid">
            {products.map((product) => {
              const mainImage = getMainImage(product.images);
              const discount = product.original_price && product.price < product.original_price
                ? calculateDiscount(product.price, product.original_price)
                : null;

              return (
                <Link key={product.id} to={`/produto/${product.id}`} className="product-card">
                  {discount && <div className="product-badge">-{discount}%</div>}
                  <img
                    src={mainImage}
                    alt={product.name}
                    className="product-image"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `data:image/svg+xml,%3Csvg width='200' height='200' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='200' height='200' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dominant-baseline='middle' font-family='Arial, sans-serif' font-size='12' fill='%23999'%3EProduto%3C/text%3E%3C/svg%3E`;
                    }}
                  />
                  <div className="product-info">
                    <div className="product-name">{product.name}</div>
                    <div className="product-price">
                      <span className="current-price">{formatPrice(product.price)}</span>
                      {product.original_price && product.original_price > product.price && (
                        <span className="old-price">{formatPrice(product.original_price)}</span>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

export default Catalogo;

